import React, { useState, useEffect } from "react";
import { Button, Input, Tag, Modal, Space } from "antd";
import Table from "../../../core/common/dataTable/index";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import api from "../../../core/axios/axiosInstance";

const { Search } = Input;

const STATUS_COLORS = {
  completed: "success",
  scheduled: "processing",
  failed: "error",
};

export default function AdminWhatsappCampaigns() {
  const navigate = useNavigate();
  const route = all_routes;

  const userProfile = useSelector((state) => state.profile);
  const isWabaConnected = userProfile?.accounts?.whatsapp?.isConnected;

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCampaigns = async (p = page, ps = pageSize, s = search) => {
    setLoading(true);
    try {
      const res = await api.post("/superAdmin/whatsapp/campaigns", { page: p, limit: ps, search: s });
      setCampaigns(res.data?.campaigns || []);
      setTotal(res.data?.count || 0);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, [page, pageSize, search]);

  const handleDelete = async () => {
    if (!selectedCampaign) return;
    setDeleting(true);
    try {
      await api.delete("api/whatsapp/delete/campaigns/", { data: { campaignId: selectedCampaign.campaignId } });
      setDeleteModal(false);
      fetchCampaigns();
    } catch {
      /* ignore */
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: "Campaign Name",
      dataIndex: "campaignName",
      render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>,
    },
    {
      title: "Template",
      dataIndex: "templateName",
    },
    {
      title: "Recipients",
      dataIndex: "total",
      render: (n) => <Tag>{n}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Scheduled At",
      dataIndex: "scheduledAt",
      render: (d) => d ? new Date(d).toLocaleString() : "Immediately",
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => navigate(route.adminWhatsappCampaignDetails, { state: { record } })}
          >
            View
          </Button>
          <Button
            size="small"
            danger
            onClick={() => { setSelectedCampaign(record); setDeleteModal(true); }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (!isWabaConnected) {
    return (
      <div className="page-wrapper">
        <div className="content h-100">
          <div
            style={{
              height: "70vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <h3>WhatsApp is not connected</h3>
            <p style={{ color: "#888", marginBottom: 20 }}>
              Please configure WhatsApp to start chatting.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/admin/whatsapp/setting")}
            >
              Go to WhatsApp Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card mt-3">
          <div className="card-body pb-2">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="page-title mb-0">WhatsApp Campaigns</h4>
              <Button
                type="primary"
                style={{ background: "#6366f1", borderColor: "#6366f1" }}
                onClick={() => navigate(route.adminWhatsappAddCampaign)}
              >
                + Create Campaign
              </Button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <Search
              placeholder="Search campaigns…"
              style={{ maxWidth: 300, marginBottom: 16 }}
              onSearch={(v) => { setSearch(v); setPage(1); }}
              allowClear
            />
            <Table
              columns={columns}
              dataSource={campaigns}
              loading={loading}
              rowKey="campaignId"
              pagination={{
                current: page,
                pageSize,
                total,
                onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                showSizeChanger: true,
              }}
            />
          </div>
        </div>

        <Modal
          title="Delete Campaign"
          open={deleteModal}
          onCancel={() => setDeleteModal(false)}
          onOk={handleDelete}
          okButtonProps={{ danger: true, loading: deleting }}
          okText="Delete"
        >
          Are you sure you want to delete <strong>{selectedCampaign?.campaignName}</strong>?
        </Modal>
      </div>
    </div>
  );
}
